import type { FrameOutput } from '@meteoradar/shared/contract/frame.schema';
import { formatFrameDateTime } from '../format.js';

const LABEL_FONT_PX = 14;
const LABEL_PADDING = 6;
const LABEL_MARGIN = 8;
const LABEL_LINE_HEIGHT = 18;
const LABEL_BOX_ALPHA = 0.75;
const ATTRIBUTION = 'Datos: Euskalmet';

export type CompositeSize = { width: number; height: number };

export const composedFileName = (product: string, iso: string, extension: string): string => {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  const day = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}${pad(date.getMinutes())}`;
  return `meteoradar-${product}-${day}-${time}.${extension}`;
};

export const fitSize = (reference: CompositeSize, maxSize: number | null): CompositeSize => {
  if (maxSize === null || Math.max(reference.width, reference.height) <= maxSize) {
    return { width: reference.width, height: reference.height };
  }
  const scale = maxSize / Math.max(reference.width, reference.height);
  return { width: Math.round(reference.width * scale), height: Math.round(reference.height * scale) };
};

export const drawComposite = (
  context: CanvasRenderingContext2D,
  layers: ImageBitmap[],
  frame: FrameOutput,
  productLabel: string,
  size: CompositeSize,
): void => {
  context.clearRect(0, 0, size.width, size.height);
  for (const layer of layers) {
    context.drawImage(layer, 0, 0, size.width, size.height);
  }
  const lines = [ `${formatFrameDateTime(frame.ts)} · ${productLabel}`, ATTRIBUTION ];
  context.font = `${LABEL_FONT_PX}px system-ui, sans-serif`;
  context.textBaseline = 'top';
  const boxWidth = Math.max(...lines.map(line => context.measureText(line).width)) + LABEL_PADDING * 2;
  const boxHeight = lines.length * LABEL_LINE_HEIGHT + LABEL_PADDING * 2;
  context.fillStyle = `rgba(15, 23, 42, ${LABEL_BOX_ALPHA})`;
  context.fillRect(LABEL_MARGIN, LABEL_MARGIN, boxWidth, boxHeight);
  context.fillStyle = '#f8fafc';
  lines.forEach((line, index) => {
    context.fillText(line, LABEL_MARGIN + LABEL_PADDING, LABEL_MARGIN + LABEL_PADDING + index * LABEL_LINE_HEIGHT);
  });
};

export type CompositeCanvas = { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D };

export const createCompositeCanvas = (size: CompositeSize): CompositeCanvas => {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) {
    throw new Error('Canvas 2D context unavailable');
  }
  return { canvas, context };
};

export const composeFrame = async (layers: ImageBitmap[], frame: FrameOutput, productLabel: string): Promise<Blob> => {
  const reference = layers[0];
  if (reference === undefined) {
    throw new Error('Nothing to compose');
  }
  const size = fitSize(reference, null);
  const { canvas, context } = createCompositeCanvas(size);
  drawComposite(context, layers, frame, productLabel, size);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => blob === null ? reject(new Error('toBlob failed')) : resolve(blob), 'image/png');
  });
};

export const deliverFile = async (blob: Blob, fileName: string): Promise<void> => {
  const file = new File([ blob ], fileName, { type: blob.type });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [ file ] })) {
    try {
      await navigator.share({ files: [ file ], title: fileName });
      return;
    } catch {
      return;
    }
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
